/* eslint-disable @typescript-eslint/lines-between-class-members */
import { MintableToken, isOneTokenV1Contract } from 'constants/contracts';
import { ContractTxAsyncActionResponse } from 'common/models/crypto';
import { IchiInsights } from 'common/models/Insights';
import { isError } from 'lodash';
import { ContractUtils } from 'utils/contractUtils';
import { CryptoUtils, GAS_SETTINGS } from 'utils/cryptoUtils';
import { isUserRejectedError, isViemBaseError } from 'utils/errorUtils';
import { NumberUtils } from 'utils/numberUtils';
import { StorageUtils } from 'utils/storageUtils';
import { TransactionUtils } from 'utils/transactionUtils';
import { RedeemEvent } from 'xstate/redeemEvent';
import { Address, readContract } from '@wagmi/core';
import { oneTokenV1 } from 'constants/abis/oneTokenV1';
import { writeContract } from '@wagmi/core';
import { BaseError, UserRejectedRequestError } from 'viem';

export namespace RedeemApi {
  export type GetRedeemDataResponse = {
    redeemFee: number;
    collateralTokenBN: bigint;
    // collateralTokenUsd: number;
    isSufficientOneToken: boolean;
    // oneTokenAllowanceNumber: number;
    oneTokenAllowanceBN: bigint;
    isOneTokenApproved: boolean;
    message?: string;
  };

  export type RedeemResponse = {
    amountRedeemed: number;
    message?: string;
  };

  type RedeemArgs = {
    amount?: string;
    redeemFee?: number;
    accountAddress: string;
    chainId: number;
    contractContainer: MintableToken;
  };

  export interface IRedeem {
    getRedeemData(args: RedeemArgs): Promise<GetRedeemDataResponse>;
    redeem(args: RedeemArgs): Promise<ContractTxAsyncActionResponse>;
    resetOneTokenAllowance(args: RedeemArgs): Promise<boolean>;
    approveOneTokenAllowance(args: RedeemArgs): Promise<boolean>;
  }

  export class OneTokenRedeem implements IRedeem {
    private static instance: IRedeem;
    static maxApproval = 2n ** 256n - 1n;
    // These will primarily be used for local storage lookups for determining if there
    // are in-flight transactions
    static redeemFN = 'redeem';
    static resetOneTokenAllowanceFN = 'resetOneTokenAllowance';
    static approveOneTokenAllowanceFN = 'approveOneTokenAllowance';

    private constructor() {}

    public static getInstance(): IRedeem {
      if (!OneTokenRedeem.instance) {
        OneTokenRedeem.instance = new OneTokenRedeem();
      }
      return OneTokenRedeem.instance;
    }

    async getRedeemData(args: RedeemArgs): Promise<GetRedeemDataResponse> {
      if (args.contractContainer && !args.contractContainer.token) {
        const message = 'No token contract container provided';
        const error = new Error(message);
        console.warn(error);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'redeemApi:getRedeemData',
          },
        });
        throw error;
      }
      const redeemFee = args.redeemFee || 0;
      let collateralTokenBN = 0n;
      let isSufficientOneToken = false;
      let isOneTokenApproved = false;
      let oneTokenAllowanceBN = 0n;

      const tokenInfo = args.contractContainer;

      if (args.amount) {
        // const amountNumber = Number(args.amount);
        const amountNumber = NumberUtils.stringToBN(args.amount, 18n);

        // const collateralUSDValue = amountNumber * (1 - redeemFee);
        // collateralTokenUsd = collateralUSDValue;
        const collateralUSDValue =
          (amountNumber * BigInt((1 - redeemFee) * 10000)) / 10000n;
        collateralTokenBN = collateralUSDValue;

        // Check if one token is approved and there is enough of it
        if (isOneTokenV1Contract(tokenInfo.token, args)) {
          const oneTokenAllowance = args.accountAddress
            ? await readContract({
                address: tokenInfo.token.address,
                abi: oneTokenV1,
                functionName: 'allowance',
                args: [args.accountAddress as Address, tokenInfo.token.address],
                chainId: args.chainId,
              })
            : 0n;

          oneTokenAllowanceBN = oneTokenAllowance;
          isOneTokenApproved =
            oneTokenAllowanceBN > 0n && oneTokenAllowanceBN >= amountNumber;

          const oneTokenBalance = args.accountAddress
            ? await readContract({
                address: tokenInfo.token.address,
                abi: oneTokenV1,
                functionName: 'balanceOf',
                args: [args.accountAddress as Address],
                chainId: args.chainId,
              })
            : 0n;

          isSufficientOneToken = oneTokenBalance >= amountNumber;
        }
      }

      const response: GetRedeemDataResponse = {
        redeemFee,
        collateralTokenBN,
        isSufficientOneToken,
        isOneTokenApproved,
        oneTokenAllowanceBN,
      };
      console.debug('redeem data', response);
      return response;
    }

    async redeem(args: RedeemArgs): Promise<ContractTxAsyncActionResponse> {
      let response: ContractTxAsyncActionResponse = {
        hash: null,
        output: null,
        value: null,
      };

      const functionName = RedeemApi.OneTokenRedeem.redeemFN;

      const tokenInfo = args.contractContainer;

      if (
        isOneTokenV1Contract(tokenInfo.token, args) &&
        tokenInfo.collateralToken &&
        args.amount
      ) {
        try {
          response = await TransactionUtils.checkAndHandleTx({
            functionName,
            ...args,
          });

          // hash nulled out and output set indicates a completed tx
          if (response.hash == null && response.output != null) {
            return response;
          }

          // Otherwise there is no tx in flight, execute contract call
          const stringBN = NumberUtils.stringToBN(args.amount, 18n);
          console.debug(`redeeming: ${args.amount}, stringBN: ${stringBN}`);

          const tx = await writeContract({
            address: tokenInfo.token.address,
            abi: oneTokenV1,
            functionName: 'redeem',
            args: [tokenInfo.collateralToken.address, stringBN],
            gas: CryptoUtils.calculateGasMargin(
              BigInt(GAS_SETTINGS.redeem),
              args.chainId
            ),
            account: args.accountAddress as Address,
          });

          // Now handle the tx post contract call
          response = await TransactionUtils.handlePostContractTx({
            tx,
            functionName,
            labels: {
              executing: 'Redeeming',
              success: 'Successfully Redeemed!',
              error: 'Error Redeeming!',
            },
            value: args.amount,
            ...args,
          });

          IchiInsights.trackEvent({
            event: RedeemEvent.REDEEM,
            properties: {
              ...args,
              amount: args.amount,
              amountBNString: stringBN,
              handledAt: `redeemApi:${OneTokenRedeem.redeemFN}`,
              txHash: tx.hash,
            },
          });
        } catch (err) {
          if (isUserRejectedError(err as BaseError)) {
            throw new Error((err as UserRejectedRequestError).shortMessage);
          }

          const message = `Unable to redeem ${args.amount} ${tokenInfo.token.symbol}`;
          const error = new Error(message);
          IchiInsights.trackException({
            error,
            properties: {
              ...args,
              handledAt: `redeemApi:${OneTokenRedeem.redeemFN}`,
            },
          });
          throw error;
        }
      } else {
        const message = `Could not redeem: ${tokenInfo.token.symbol}, no contract specified`;
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `redeemApi:${OneTokenRedeem.redeemFN}`,
          },
        });
        throw error;
      }

      return response;
    }

    // This function represents a blocking contract tx that supports local storage.  It will
    // block when called waiting for the tx to complete, however it still also supports local storage
    async resetOneTokenAllowance(args: RedeemArgs): Promise<boolean> {
      const tokenInfo = args.contractContainer;
      if (!tokenInfo.token) {
        const message = 'No one token contract defined!';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `redeemApi:${OneTokenRedeem.resetOneTokenAllowanceFN}`,
          },
        });
        throw error;
      }

      const functionName = OneTokenRedeem.resetOneTokenAllowanceFN;

      try {
        const storedTx = await StorageUtils.getTx({
          functionName: OneTokenRedeem.resetOneTokenAllowanceFN,
          ...args,
        });

        // If there is a tx already in progress, wait for it here
        if (storedTx) {
          const didSucceed = await ContractUtils.waitForTransactionBoolean({
            functionName: OneTokenRedeem.resetOneTokenAllowanceFN,
            hash: storedTx.hash,
            ...(args as any),
          });
          return didSucceed;
        }

        // If no prior tx, execute contract function

        const allowance = await readContract({
          address: tokenInfo.token.address,
          abi: oneTokenV1,
          functionName: 'allowance',
          args: [args.accountAddress as Address, tokenInfo.token.address],
          chainId: args.chainId,
        });

        const tx = await writeContract({
          address: tokenInfo.token.address,
          abi: oneTokenV1,
          functionName: 'decreaseAllowance',
          args: [tokenInfo.token.address, allowance],
        });

        // Storage the tx hash in local storage
        if (tx) {
          await StorageUtils.storeTx({
            hash: tx.hash,
            functionName,
            labels: {
              executing: 'Resetting Allowance...',
              success: 'Successfully reset Allowance...',
              error: 'Error resetting Allowance...',
            },
            value: args.amount,
            ...args,
          });

          // Wait for the transaction to complete
          const didSucceed = await ContractUtils.waitForTransactionBoolean({
            functionName: OneTokenRedeem.resetOneTokenAllowanceFN,
            hash: tx.hash,
            ...(args as any),
          });
          return didSucceed;
        }

        throw new Error(
          'Unable to reset one token allowance due to infuraProvider being null'
        );
      } catch (error) {
        if (isUserRejectedError(error as BaseError)) {
          throw new Error((error as UserRejectedRequestError).shortMessage);
        } else if (isError(error)) {
          IchiInsights.trackException({
            error,
            properties: {
              ...args,
              handledAt: `redeemApi:${OneTokenRedeem.resetOneTokenAllowanceFN}`,
            },
          });
          throw isViemBaseError(error as BaseError)
            ? new Error((error as BaseError).shortMessage)
            : error;
        }
      }
      return false;
    }

    async approveOneTokenAllowance(args: RedeemArgs): Promise<boolean> {
      const tokenInfo = args.contractContainer;
      if (!tokenInfo.token) {
        const message = 'No one token contract defined!';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `redeemApi:${OneTokenRedeem.approveOneTokenAllowanceFN}`,
          },
        });
        throw error;
      }

      const functionName = OneTokenRedeem.approveOneTokenAllowanceFN;

      try {
        // First let's see if the allwance is already approved for the allowance
        const allowance = await readContract({
          address: tokenInfo.token.address,
          abi: oneTokenV1,
          functionName: 'allowance',
          args: [args.accountAddress as Address, tokenInfo.token.address],
          chainId: args.chainId,
        });

        if (OneTokenRedeem.maxApproval === allowance) {
          return true;
        }

        const storedTx = await StorageUtils.getTx({
          functionName: OneTokenRedeem.approveOneTokenAllowanceFN,
          ...args,
        });

        // If there is a tx already in progress, wait for it here
        if (storedTx) {
          const didSucceed = await ContractUtils.waitForTransactionBoolean({
            functionName: OneTokenRedeem.approveOneTokenAllowanceFN,
            hash: storedTx.hash,
            ...(args as any),
          });
          return didSucceed;
        }

        // If no prior tx, execute contract function
        const tx = await writeContract({
          address: tokenInfo.token.address,
          abi: oneTokenV1,
          functionName: 'approve',
          args: [tokenInfo.token.address, OneTokenRedeem.maxApproval],
          gas: CryptoUtils.calculateGasMargin(
            BigInt(GAS_SETTINGS.approve),
            args.chainId
          ),
        });

        if (tx) {
          IchiInsights.trackEvent({
            event: RedeemEvent.APPROVE_ONE_TOKEN_ALLOWANCE,
            properties: {
              ...args,
              amount: OneTokenRedeem.maxApproval.toString(),
              handledAt: `redeemApi:${OneTokenRedeem.approveOneTokenAllowanceFN}`,
              txHash: tx.hash,
            },
          });

          // Storage the tx hash in local storage
          await StorageUtils.storeTx({
            hash: tx.hash,
            functionName,
            labels: {
              executing: 'Approving...',
              success: 'Successfully approved!',
              error: 'Error approving!',
            },
            value: args.amount,
            ...args,
          });
          // Wait for the transaction to complete
          const didSucceed = await ContractUtils.waitForTransactionBoolean({
            functionName: OneTokenRedeem.approveOneTokenAllowanceFN,
            hash: tx.hash,
            ...(args as any),
          });
          return didSucceed;
        }

        throw new Error(
          'Unable to approve one token allowance due to infuraProvider being null'
        );
      } catch (err) {
        console.error(err);
        if (isUserRejectedError(err as BaseError)) {
          throw new Error((err as UserRejectedRequestError).shortMessage);
        }
        const error = isViemBaseError(err as BaseError)
          ? new Error((err as BaseError).shortMessage)
          : err;
        if (isError(error)) {
          IchiInsights.trackException({
            error,
            properties: {
              ...args,
              handledAt: `redeemApi:${OneTokenRedeem.approveOneTokenAllowanceFN}`,
            },
          });
          throw error;
        }
      }
      return false;
    }
  }
}
